import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import './SectorsSettings.css';
import {
  LUCIDE_SERVICE_ICON_OPTIONS,
  normalizeServiceIconKey,
} from '../../constants/lucideServiceSectionIcons';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const shirtOption =
  LUCIDE_SERVICE_ICON_OPTIONS.find((o) => o.value === 'shirt') ||
  LUCIDE_SERVICE_ICON_OPTIONS.find((o) => o.value === 'briefcase');

function selectedSectorIconOption(iconKey) {
  const k = normalizeServiceIconKey(iconKey);
  return (
    LUCIDE_SERVICE_ICON_OPTIONS.find((o) => o.value === k) || shirtOption
  );
}

const iconSelectStyles = {
  control: (base) => ({
    ...base,
    backgroundColor: 'rgba(1, 7, 39, 0.8)',
    borderColor: 'rgba(255, 255, 255, 0.25)',
    minHeight: 38,
    boxShadow: 'none',
    '&:hover': { borderColor: 'rgba(255, 255, 255, 0.35)' },
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: '#051139',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    zIndex: 20,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menuList: (base) => ({ ...base, maxHeight: 280, padding: 4 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused
      ? 'rgba(255, 255, 255, 0.12)'
      : 'transparent',
    color: '#ffffff',
    cursor: 'pointer',
    fontSize: 13,
  }),
  singleValue: (base) => ({ ...base, color: '#ffffff' }),
  input: (base) => ({ ...base, color: '#ffffff' }),
  placeholder: (base) => ({ ...base, color: 'rgba(246, 246, 246, 0.55)' }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: 'rgba(246, 246, 246, 0.75)',
  }),
};

function SectorsSettings() {
  const [header, setHeader] = useState({
    eyebrow: '',
    title: '',
    description: '',
    imageUrl: '',
  });
  const [sectors, setSectors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchSectors = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/sectors`);
        if (response.data?.header) {
          setHeader(response.data.header);
        }
        if (Array.isArray(response.data?.sectors)) {
          setSectors(response.data.sectors);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSectors();
  }, []);

  const handleHeaderChange = (field, value) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
  };

  const handleSectorChange = (index, field, value) => {
    setSectors((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleSectorDelete = (index) => {
    setSectors((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSectorAdd = () => {
    setSectors((prev) => [
      ...prev,
      {
        id: Date.now().toString(),
        title: '',
        iconKey: 'shirt',
      },
    ]);
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/sectors/upload-image`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (response.data?.url) {
        handleHeaderChange('imageUrl', response.data.url);
        setMessage('Sectors image uploaded successfully.');
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setMessage('Unable to upload image. Please try again.');
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage('');

    try {
      const normalizedSectors = sectors.map((s) => ({
        ...s,
        iconKey: normalizeServiceIconKey(s.iconKey),
      }));
      const response = await axios.put(`${API_BASE_URL}/api/sectors`, {
        header,
        sectors: normalizedSectors,
      });

      if (response.data?.header) {
        setHeader(response.data.header);
      }
      if (Array.isArray(response.data?.sectors)) {
        setSectors(response.data.sectors);
      }

      setMessage('Sectors section updated successfully.');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setMessage('Unable to save sectors section. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <p className="sectors-settings-loading">Loading sectors…</p>;
  }

  return (
    <div className="sectors-settings">
      <div className="sectors-settings-header">
        <div>
          <h2 className="sectors-settings-title">Sectors section</h2>
          <p className="sectors-settings-subtitle">
            Edit the header text, image, and sector chips shown in the “Sectors we serve”
            block on the home page.
          </p>
        </div>
        <button
          type="button"
          className="sectors-settings-primary-btn"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      <div className="sectors-settings-card">
        <h3 className="sectors-settings-section-title">Header content</h3>
        <div className="sectors-settings-grid">
          <div className="sectors-settings-field">
            <label className="sectors-settings-label">
              Eyebrow
              <input
                type="text"
                className="sectors-settings-input"
                value={header.eyebrow}
                onChange={(e) => handleHeaderChange('eyebrow', e.target.value)}
                placeholder="SECTORS WE SERVE"
              />
            </label>
          </div>

          <div className="sectors-settings-field">
            <label className="sectors-settings-label">
              Title
              <textarea
                className="sectors-settings-textarea"
                rows={2}
                value={header.title}
                onChange={(e) => handleHeaderChange('title', e.target.value)}
                placeholder={'Expertise Across\nKey Industries.'}
              />
            </label>
          </div>

          <div className="sectors-settings-field sectors-settings-field-wide">
            <label className="sectors-settings-label">
              Description
              <textarea
                className="sectors-settings-textarea"
                rows={3}
                value={header.description}
                onChange={(e) =>
                  handleHeaderChange('description', e.target.value)
                }
                placeholder="Short paragraph explaining your cross-sector expertise."
              />
            </label>
          </div>

          <div className="sectors-settings-field sectors-settings-field-wide">
            <label className="sectors-settings-label">
              Header image URL
              <input
                type="text"
                className="sectors-settings-input"
                value={header.imageUrl}
                onChange={(e) =>
                  handleHeaderChange('imageUrl', e.target.value)
                }
                placeholder="https://example.com/sectors-image.jpg"
              />
            </label>
            <label className="sectors-settings-label">
              Or upload image
              <input
                type="file"
                accept="image/*"
                className="sectors-settings-input"
                onChange={(e) =>
                  handleImageUpload(e.target.files?.[0] || null)
                }
              />
            </label>
          </div>
        </div>
      </div>

      <div className="sectors-settings-card" style={{ marginTop: 24 }}>
        <div className="sectors-settings-header-row">
          <h3 className="sectors-settings-section-title">Sectors list</h3>
          <button
            type="button"
            className="sectors-settings-secondary-btn"
            onClick={handleSectorAdd}
          >
            + Add sector
          </button>
        </div>

        <div className="sectors-settings-list">
          {sectors.map((sector, index) => (
            <div key={sector.id || index} className="sectors-settings-row">
              <div className="sectors-settings-col sectors-settings-col-main">
                <label className="sectors-settings-label">
                  Title
                  <input
                    type="text"
                    className="sectors-settings-input"
                    value={sector.title}
                    onChange={(e) =>
                      handleSectorChange(index, 'title', e.target.value)
                    }
                    placeholder="Fintech"
                  />
                </label>
              </div>

              <div className="sectors-settings-col sectors-settings-col-icon">
                <label className="sectors-settings-label">
                  Icon
                  <Select
                    className="sectors-settings-icon-select"
                    classNamePrefix="sectors-settings-icon-select"
                    options={LUCIDE_SERVICE_ICON_OPTIONS}
                    value={selectedSectorIconOption(sector.iconKey)}
                    onChange={(opt) =>
                      handleSectorChange(index, 'iconKey', opt?.value || 'shirt')
                    }
                    isSearchable
                    placeholder="Search icons…"
                    menuPlacement="auto"
                    menuPortalTarget={
                      typeof document !== 'undefined' ? document.body : null
                    }
                    styles={iconSelectStyles}
                    formatOptionLabel={(opt) => (
                      <span title={opt.value}>
                        {opt.label}{' '}
                        <span className="sectors-settings-icon-slug">
                          ({opt.value})
                        </span>
                      </span>
                    )}
                  />
                </label>

                <button
                  type="button"
                  className="sectors-settings-delete-btn"
                  onClick={() => handleSectorDelete(index)}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}

          {sectors.length === 0 && (
            <p className="sectors-settings-empty">
              No sectors configured yet. Click “Add sector” to create one.
            </p>
          )}
        </div>
      </div>

      {message && <p className="sectors-settings-message">{message}</p>}
    </div>
  );
}

export default SectorsSettings;

